import clsx from "clsx";
import { rgbToCss } from "utils/rgbToCss";
import { TruncatedTextTooltip } from "components/common/TruncatedTextTooltip";
import type { LayoutListRowProps } from "./types";

const ROW_CLASS_NAME =
  "w-full flex gap-2 p-3 rounded-md border border-gray-200 bg-white text-left";

export const LayoutListRow = ({
  index,
  name,
  colorRgb,
  badge,
  detail,
  onClick,
  isSelected,
  onHoverChange,
  action,
}: LayoutListRowProps) => {
  const isStacked = badge !== undefined;

  const content = (
    <>
      <span className="shrink-0 min-w-5 text-sm leading-5 tabular-nums text-gray-400">
        {index}.
      </span>
      {colorRgb && (
        <span
          className="shrink-0 mt-[5px] h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: rgbToCss(colorRgb) }}
        />
      )}
      <span
        className={clsx(
          "flex-1 min-w-0 flex",
          isStacked ? "flex-col gap-1" : "items-center gap-2",
        )}
      >
        <TruncatedTextTooltip
          text={name}
          className="flex-1 min-w-0 text-sm leading-5 font-medium text-gray-900"
        />
        <span className="shrink-0 flex items-center gap-2">
          {badge && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-600">
              {badge}
            </span>
          )}
          <span className="text-sm text-gray-500">{detail}</span>
        </span>
      </span>
    </>
  );

  return (
    <li
      className="flex items-stretch gap-1"
      onMouseEnter={onHoverChange && (() => onHoverChange(true))}
      onMouseLeave={onHoverChange && (() => onHoverChange(false))}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          aria-pressed={isSelected}
          className={clsx(
            ROW_CLASS_NAME,
            "transition-colors",
            isSelected
              ? "!border-blue-300 !bg-blue-50 hover:!bg-blue-100"
              : "hover:bg-gray-50",
          )}
        >
          {content}
        </button>
      ) : (
        <div
          className={clsx(
            ROW_CLASS_NAME,
            onHoverChange &&
              "cursor-pointer hover:bg-gray-50 transition-colors",
          )}
        >
          {content}
        </div>
      )}
      {action && <span className="shrink-0 flex items-center">{action}</span>}
    </li>
  );
};
